import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { Alert, Button, Card, Chip, Input, Label, ListBox, Select, SearchField, Table, Tabs, TextField } from '@heroui/react'
import { BookDrawer } from '../ui/BookDrawer'
import { Chrome } from '../ui/Chrome'
import { Icon } from '../ui/icons'
import { rich, useHallToast } from '../ui/hallParts'
import { useStaffText } from '../ui/StaffText'
import { EmptyState } from '../ui/parts'
import { api } from '../lib/api'
import { normaliseCode } from '../lib/appointmentCode'
import { bookingRefusal, reasonText } from '../lib/appointmentImport'
import { BOOKINGS_MODULE, bookingService, bookingsAccess, bookingsHeld, bookingsShutLine, tally, valuesLine } from '../lib/bookings'
import { addDays, clockIn, editable, matchesBooking, rowStatus, type RowStatus, type StatusKind } from '../lib/bookingStatus'
import {
  CANCEL_REASON_KEY,
  CANCEL_REASONS,
  cancelReasonText,
  choosesBranch,
  daysToReach,
  slotFlow,
  upcomingRanges,
  type CancelReason,
} from '../lib/bookingFlow'
import { optionOn } from '../lib/orgOptions'
import { bookingLabel, codeLineUnder, deskActions, showsDetails, sourceKey } from '../lib/bookingDesk'
import { useNow } from '../lib/clock'
import { explain } from '../lib/staffErrors'
import { formatMoment, nameIn } from '../lib/staffText'
import { useBranchLive } from '../lib/useBranch'
import type { Appointment, AppointmentState, CatalogueEntry, CheckInRules, Identity, Service, VisitField } from '../lib/types'
import type { StaffKey } from '../lib/staffText'
import '../styles/hall.css'
import '../styles/desk.css'
import '../styles/reception.css'
import '../styles/appointments.css'
import { Confirm, FormModal, StatusChip } from './receptionKit'

/** How each state but arrival is said; an arrival says its number. */
const STATE_KEY: Record<Exclude<AppointmentState, 'arrived'>, StaffKey> = {
  booked: 'appointments.state.booked',
  cancelled: 'appointments.state.cancelled',
  missed: 'appointments.state.missed',
}

/** A state other than arrived; a cancelled booking a branch edge arrived anyway names its number. */
function endedText(a: Appointment, say: ReturnType<typeof useStaffText>['say']): string {
  if (a.state === 'cancelled' && a.displayNumber) return say('appointments.state.cancelledArrived', { number: a.displayNumber })
  return say(STATE_KEY[a.state as Exclude<AppointmentState, 'arrived'>])
}

const PILL_KEY: Record<StatusKind, StaffKey> = {
  upcoming: 'appointments.st.upcoming',
  early: 'appointments.st.early',
  onTime: 'appointments.st.onTime',
  lateAllowed: 'appointments.st.lateAllowed',
  late: 'appointments.st.late',
  noShow: 'appointments.st.noShow',
  checkedIn: 'appointments.st.checkedIn',
  cancelled: 'appointments.st.cancelled',
}

/** Days Show more adds to the Upcoming list. */
const UPCOMING_STEP = 7

/** A reference for a booking made here rather than sent by a booking system:
 *  short, letters and digits, so it also works as the customer's code. */
function newReference(): string {
  const bytes = new Uint8Array(4)
  crypto.getRandomValues(bytes)
  return 'W' + Array.from(bytes, (b) => (b % 36).toString(36)).join('').toUpperCase() + Date.now().toString(36).slice(-3).toUpperCase()
}

/**
 * Today's bookings and the ones after it: check one in, add one by hand, change
 * or cancel one, and how long bookings are kept.
 */
export function Appointments({
  identity,
  onNavigate,
  onSignOut,
}: {
  identity: Identity
  onNavigate: (to: string) => void
  onSignOut: () => void
}) {
  const { lang, say } = useStaffText()
  const live = useBranchLive('appointments', { lang })
  const branchId = live.branch?.id ?? ''
  const zone = live.branch?.timezone
  const mayChangePolicy = identity.permissions.includes('catalogue.manage')
  const mayArrive = identity.permissions.includes('ticket.issue')
  const now = useNow()
  const toast = useHallToast()

  const [view, setView] = useState<'today' | 'upcoming'>('today')
  const [today, setToday] = useState('')
  const [todayList, setTodayList] = useState<Appointment[]>([])
  // "Nobody booked" is said only once the list has been read.
  const [todayRead, setTodayRead] = useState(false)
  const [rules, setRules] = useState<CheckInRules | undefined>()
  const [days, setDays] = useState(UPCOMING_STEP)
  const [upList, setUpList] = useState<Appointment[]>([])
  const [upRead, setUpRead] = useState('')
  const [declared, setDeclared] = useState<VisitField[]>([])
  const [services, setServices] = useState<Service[]>([])
  const [keepDays, setKeepDays] = useState<number | null>(null)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const [find, setFind] = useState('')
  const [earlier, setEarlier] = useState(false)
  const [opened, setOpened] = useState<{ id: string; ask?: boolean } | null>(null)
  const [drawer, setDrawer] = useState<'' | 'add' | 'book' | 'settings'>('')
  const [moving, setMoving] = useState<Appointment | null>(null)
  const [editing, setEditing] = useState<Appointment | null>(null)
  // Booked against offered today, for the services offered online.
  const [dayLoad, setDayLoad] = useState<{ offered: number; booked: number } | null>(null)
  // Whether this branch holds Appointments. Unknown (null) shows the tools: a
  // failed read must not hide a branch's own buttons; the server still refuses.
  const [held, setHeld] = useState<boolean | null>(null)
  useEffect(() => {
    if (!branchId) return
    setHeld(null)
    setTodayRead(false)
    setUpRead('')
    api
      .heldModules(branchId)
      .then((r) => setHeld(r.held.includes(BOOKINGS_MODULE)))
      .catch(() => setHeld(null))
  }, [branchId])
  const mayBook = held !== false
  // Online booking off: its slots are refused, so the tally and Change go with it.
  const bookOnline = optionOn(identity, 'onlineBooking')
  // Bought but held back by the plan or licence: the list stays, the server refuses new bookings.
  const access = bookingsAccess(identity)
  const heldLine = bookingsHeld(access) ? bookingsShutLine(access) : null
  // Where New booking may book, whatever branch is in view; null until read.
  const [catalogue, setCatalogue] = useState<CatalogueEntry[] | null>(null)
  useEffect(() => {
    if (!bookOnline) return
    api
      .appointmentCatalogue()
      .then((r) => setCatalogue(r.branches))
      .catch(() => setCatalogue([]))
  }, [bookOnline])
  const newBooking = () => setDrawer(slotFlow(bookOnline, catalogue) ? 'book' : 'add')
  // Pressed before the catalogue came back empty: the hand-made booking instead.
  useEffect(() => {
    if (drawer === 'book' && catalogue?.length === 0) setDrawer('add')
  }, [drawer, catalogue])
  const chooseBranch = choosesBranch(live.branches.length, !identity.staff.branchIds?.length)

  const load = useCallback(async () => {
    if (!branchId) return
    const answer = await api.appointments(branchId)
    setTodayList(answer.appointments)
    setToday(answer.businessDate)
    setRules(answer.checkIn)
    setTodayRead(true)
    api.bookingDay(branchId, answer.businessDate).then(setDayLoad, () => setDayLoad(null))
    if (view !== 'upcoming') return
    const lists = await Promise.all(upcomingRanges(answer.businessDate, days).map((r) => api.appointments(branchId, undefined, r)))
    setUpList(lists.flatMap((l) => l.appointments))
    setUpRead(`${answer.businessDate}:${days}`)
  }, [branchId, view, days])

  useEffect(() => {
    void load().catch((err) => setError(explain(err, lang, 'common.unreachable')))
    // lang is left out on purpose, as Floor does: a language change must not refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load])

  // Bookings are not on the live channel: read again every minute so a kiosk arrival shows.
  useEffect(() => {
    const timer = window.setInterval(() => void load().catch(() => undefined), 60_000)
    return () => window.clearInterval(timer)
  }, [load])

  useEffect(() => {
    api
      .declaredFields()
      .then((a) => setDeclared(a.fields))
      .catch((err) => {
        setDeclared([])
        setError((prev) => prev || explain(err, lang, 'common.unreachable'))
      })
    api
      .appointmentPolicy()
      .then((p) => setKeepDays(p.keepDays))
      .catch((err) => {
        setKeepDays(null)
        setError((prev) => prev || explain(err, lang, 'common.unreachable'))
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!branchId) return
    api
      .services(branchId)
      .then((r) => setServices(r.services))
      .catch(() => setServices([]))
  }, [branchId])

  async function act(fn: () => Promise<void>) {
    setBusy(true)
    setError('')
    try {
      await fn()
    } catch (err) {
      setError(explain(err, lang, 'common.unreachable'))
    } finally {
      setBusy(false)
    }
  }

  const snapshot = live.state
  const allServices = snapshot?.services?.length ? snapshot.services : services
  const queueOf = (serviceId: string) => snapshot?.queues.find((x) => x.serviceIds?.includes(serviceId))
  const letterOf = (serviceId: string) => {
    const q = queueOf(serviceId)
    if (!q) return ''
    return q.letter || q.code.slice(0, 1)
  }
  const ticketOf = (a: Appointment) => (a.ticketId ? snapshot?.tickets.find((t) => t.id === a.ticketId) : undefined)
  const who = (a: Appointment) => a.customerName || valuesLine(a, declared) || a.reference

  const isToday = view === 'today'
  const list = isToday ? todayList : upList
  const read = isToday ? todayRead : upRead === `${today}:${days}`
  const sorted = useMemo(
    () => [...list].sort((a, b) => Date.parse(a.startsAt) - Date.parse(b.startsAt) || a.reference.localeCompare(b.reference)),
    [list],
  )
  const needle = find.trim()
  const settled = (a: Appointment) => isToday && a.state !== 'booked' && Date.parse(a.startsAt) <= now && ticketOf(a)?.state !== 'waiting'
  const folded = isToday && !needle && !earlier ? sorted.filter(settled) : []
  const shown = needle ? sorted.filter((a) => matchesBooking(a, needle)) : sorted.filter((a) => !folded.includes(a))
  const counts = tally(todayList)
  const late = todayList.filter((a) => a.state === 'booked' && Date.parse(a.startsAt) < now)
  const nowAt = isToday && !needle ? shown.findIndex((a) => Date.parse(a.startsAt) > now) : -1
  const time = (at: string | number) => formatMoment(new Date(at), lang, { time: true, zone })
  const dayName = (d: string) => formatMoment(new Date(`${d}T12:00:00Z`), lang, { weekday: true, date: true, zone: 'UTC' })
  const statusOf = (a: Appointment) => rowStatus(a, now, a.businessDate === today, rules)

  function arrive(a: Appointment, regular = false) {
    void act(async () => {
      const result = await api.arrive(branchId, { appointmentId: a.id, ...(regular ? { regular: true } : {}) })
      setOpened(null)
      toast.show(say('hall.done.arrived', { who: who(a), number: result.ticket.displayNumber }))
      await load()
    })
  }

  function cancel(a: Appointment, reason: string) {
    void act(async () => {
      if (a.source === 'online' || a.source === 'reception') await api.cancelBooking(branchId, a.id, reason)
      else await api.cancelAppointment(branchId, a.reference, reason)
      setOpened(null)
      toast.show(say('hall.done.cancelled', { reference: bookingLabel(a) }))
      await load()
    })
  }

  const open = opened ? list.find((a) => a.id === opened.id) : undefined
  const codeText = (a: Appointment) => {
    const { reference, code } = codeLineUnder(a, who(a))
    return [reference, code].filter(Boolean).join(' · ')
  }
  const mayMove = (a: Appointment) => bookOnline && deskActions(a, mayBook).move
  // Online and reception bookings move on the slot grid; a file's booking is saved again.
  const change = (a: Appointment) => {
    if (mayMove(a)) return () => setMoving(a)
    if (editable(a, mayBook)) return () => setEditing(a)
    return undefined
  }

  function statusSub(a: Appointment, s: RowStatus): string {
    if (s.kind === 'upcoming' && s.opensAt !== undefined) return say('appointments.sub.opensAt', { time: time(s.opensAt) })
    if (s.beyond !== undefined) return say('appointments.sub.beyond', { n: s.beyond })
    if (s.kind === 'checkedIn' && a.displayNumber) {
      return ticketOf(a)?.state === 'waiting'
        ? say('hall.st.waiting', { number: a.displayNumber })
        : say('appointments.sub.ticket', { number: a.displayNumber })
    }
    if (s.kind === 'cancelled' && a.displayNumber) return say('appointments.sub.cameAs', { number: a.displayNumber })
    return ''
  }

  // After a booking is made or saved: the list that holds its day, widened to reach it.
  function showDay(date: string) {
    const next = date === today ? 'today' : 'upcoming'
    if (next === 'upcoming' && today) setDays(daysToReach(today, date, days, UPCOMING_STEP))
    if (next === view) void load().catch(() => undefined)
    else setView(next)
  }

  const nowRow = (
    <Table.Row key="now" id="now" className="appt-now" aria-hidden="true">
      <Table.Cell colSpan={5}>
        <span>{say('hall.nowAt', { time: time(now) })}</span>
      </Table.Cell>
    </Table.Row>
  )

  // The table's rows in order: the fold, the day headings, the "now" line, the bookings.
  const rows: ReactNode[] = []
  if (isToday && !needle && sorted.some(settled)) {
    rows.push(
      <Table.Row key="fold" id="fold" className="appt-foldrow">
        <Table.Cell colSpan={5}>
          <Button variant="ghost" size="sm" onPress={() => setEarlier(!earlier)} aria-expanded={earlier}>
            <Icon name={earlier ? 'up' : 'down'} size={14} />
            {earlier ? say('hall.hideEarlier') : say('hall.showEarlier', { n: folded.length })}
          </Button>
        </Table.Cell>
      </Table.Row>,
    )
  }
  shown.forEach((a, i) => {
    const s = statusOf(a)
    const sub = statusSub(a, s)
    const q = queueOf(a.serviceId)
    const src = sourceKey(a)
    const actions = deskActions(a, mayBook)
    const newDay = !isToday && (i === 0 || shown[i - 1]!.businessDate !== a.businessDate)
    if (i === nowAt) rows.push(nowRow)
    if (newDay) {
      rows.push(
        <Table.Row key={`day-${a.businessDate}`} id={`day-${a.businessDate}`} className="appt-dayrow">
          <Table.Cell colSpan={5}>{dayName(a.businessDate)}</Table.Cell>
        </Table.Row>,
      )
    }
    rows.push(
      <Table.Row key={a.id} id={a.id} className={`appt-row appt-row--${a.state}`} onAction={() => setOpened({ id: a.id })}>
        <Table.Cell className="appt-row__time">{time(a.startsAt)}</Table.Cell>
        <Table.Cell>
          <strong className="appt-row__who">{who(a)}</strong>
          <span className="appt-row__line">
            {[codeText(a), a.contact].filter(Boolean).join(' · ')}
            {src && <Chip size="sm">{say(src)}</Chip>}
          </span>
        </Table.Cell>
        <Table.Cell>
          <span className="appt-row__svc">
            {q && <span className="rx-qt">{letterOf(a.serviceId)}</span>}
            <b>{bookingService(a, allServices, lang)}</b>
          </span>
          {q && <span className="appt-row__line">{nameIn(q.name, lang, q.code)}</span>}
        </Table.Cell>
        <Table.Cell>
          <StatusChip tone={s.tone}>{say(PILL_KEY[s.kind], { n: s.lateBy ?? 0 })}</StatusChip>
          {sub && <span className="appt-row__line">{sub}</span>}
        </Table.Cell>
        <Table.Cell className="appt-row__act">
          {mayArrive && s.checkIn && (
            <Button variant="secondary" size="sm" isDisabled={busy} onPress={() => arrive(a)}>
              {say('appointments.checkIn')}
            </Button>
          )}
          {mayArrive && s.regular && (
            <Button variant="tertiary" size="sm" isDisabled={busy} onPress={() => arrive(a, true)}>
              {say('appointments.regular')}
            </Button>
          )}
          {change(a) && (
            <Button variant="ghost" size="sm" isDisabled={busy} onPress={change(a)}>
              {say('appointments.change')}
            </Button>
          )}
          {actions.cancel && (
            <Button variant="ghost" size="sm" className="rx-danger" isDisabled={busy} onPress={() => setOpened({ id: a.id, ask: true })}>
              {say('common.cancel')}
            </Button>
          )}
        </Table.Cell>
      </Table.Row>,
    )
  })
  if (nowAt === -1 && isToday && !needle) rows.push(nowRow)

  return (
    <Chrome
      title={say('entry.app.appointments')}
      staff={identity.staff}
      branch={live.branch}
      branches={live.branches}
      status={live.status}
      onChooseBranch={live.chooseBranch}
      onNavigate={onNavigate}
      onSignOut={onSignOut}
    >
      <main className="page appt rx">
        {error && (
          <Alert status="danger">
            <Alert.Indicator />
            <Alert.Content>
              <Alert.Title>{error}</Alert.Title>
            </Alert.Content>
          </Alert>
        )}
        <header className="rx-head">
          <div className="rx-head__text">
            <h1 className="page__title">{say('entry.app.appointments')}</h1>
          </div>
          <Button variant="tertiary" size="sm" onPress={() => setDrawer('settings')}>
            {say('hall.settings')}
          </Button>
        </header>
        {heldLine && (
          <Alert status="warning" className="appt-held">
            <Alert.Indicator />
            <Alert.Content>
              <Alert.Title>{say(heldLine)}</Alert.Title>
            </Alert.Content>
          </Alert>
        )}

        <Card className="appt-card" aria-labelledby="appt-card-title">
          <Card.Header className="appt-card__head">
            <div className="appt-card__title">
              <Card.Title id="appt-card-title" render={(props) => <h2 {...props} />}>
                {isToday ? say('bookings.title') : say('appointments.upcomingTitle')}
              </Card.Title>
            </div>
            <div className="appt-card__do">
              <Tabs selectedKey={view} onSelectionChange={(k) => setView(String(k) as 'today' | 'upcoming')} className="rx-tabs">
                <Tabs.ListContainer>
                  <Tabs.List aria-label={say('appointments.upcomingTitle')}>
                    <Tabs.Tab id="today">
                      {say('hall.today')}
                      <Tabs.Indicator />
                    </Tabs.Tab>
                    <Tabs.Tab id="upcoming">
                      {say('appointments.upcoming')}
                      <Tabs.Indicator />
                    </Tabs.Tab>
                  </Tabs.List>
                </Tabs.ListContainer>
              </Tabs>
              <SearchField aria-label={say('appointments.find')} variant="secondary" value={find} onChange={setFind} className="appt-find">
                <SearchField.Group>
                  <SearchField.SearchIcon />
                  <SearchField.Input placeholder={say('appointments.find')} />
                  <SearchField.ClearButton />
                </SearchField.Group>
              </SearchField>
              {mayBook ? (
                <Button variant="primary" size="sm" onPress={newBooking}>
                  <Icon name="plus" size={15} /> {say('appointments.newBooking')}
                </Button>
              ) : (
                <StatusChip tone="warn">{say('appointments.notAssigned')}</StatusChip>
              )}
            </div>
          </Card.Header>

          {isToday && rules && (
            <div className="appt-rules">
              <b>{say('appointments.rules.title')}</b>
              <span>{say('appointments.rules.text', { early: rules.earlyMin, onTime: rules.onTimeMin, late: rules.lateMin })}</span>
            </div>
          )}

          {!read ? null : sorted.length === 0 ? (
            <EmptyState
              icon={<Icon name="booking" size={18} />}
              title={isToday ? say('hall.nobodyBooked') : say('appointments.noneUpcoming')}
              action={
                mayBook ? (
                  <Button variant="secondary" size="sm" onPress={newBooking}>
                    <Icon name="plus" size={15} /> {say('appointments.newBooking')}
                  </Button>
                ) : undefined
              }
            />
          ) : shown.length === 0 && !folded.length ? (
            <EmptyState icon={<Icon name="search" size={18} />} title={say('hall.noMatch', { q: needle })} />
          ) : (
            <Table variant="secondary" className="rx-table appt-table">
              <Table.ScrollContainer>
                <Table.Content aria-label={isToday ? say('bookings.title') : say('appointments.upcomingTitle')} className="appt-t">
                  <Table.Header>
                    <Table.Column className="appt-col-time">{say('hall.time')}</Table.Column>
                    <Table.Column isRowHeader>{say('appointments.col.customer')}</Table.Column>
                    <Table.Column>{say('hall.service')}</Table.Column>
                    <Table.Column>{say('hall.status')}</Table.Column>
                    <Table.Column className="rx-col-act">
                      <span className="sr">{say('reception.col.actions')}</span>
                    </Table.Column>
                  </Table.Header>
                  <Table.Body>{rows}</Table.Body>
                </Table.Content>
              </Table.ScrollContainer>
            </Table>
          )}

          {isToday && read && todayList.length > 0 && (
            <p className="appt-tally">
              <span>{rich(say, 'hall.tally.arrived', {}, { n: <strong>{counts.arrived}</strong> })}</span>
              <span>{rich(say, 'hall.tally.toCome', {}, { n: <strong>{counts.booked - late.length}</strong> })}</span>
              {late.length > 0 && <span data-over>{rich(say, 'hall.tally.late', {}, { n: <strong>{late.length}</strong> })}</span>}
              {counts.missed > 0 && <span>{rich(say, 'hall.tally.missed', {}, { n: <strong>{counts.missed}</strong> })}</span>}
              {counts.cancelled > 0 && <span>{rich(say, 'hall.tally.cancelled', {}, { n: <strong>{counts.cancelled}</strong> })}</span>}
              {bookOnline && dayLoad && dayLoad.offered > 0 && <span>{say('booking.dayLoad', { booked: dayLoad.booked, offered: dayLoad.offered })}</span>}
            </p>
          )}
          {!isToday && read && (
            <div className="appt-more">
              <Button variant="ghost" size="sm" onPress={() => setDays(days + UPCOMING_STEP)}>
                {say('appointments.moreDays', { n: UPCOMING_STEP })}
              </Button>
            </div>
          )}
        </Card>
      </main>

      {showsDetails(open, moving) && open && (
        <BookingPanel
          key={open.id}
          a={open}
          who={who(open)}
          declared={declared}
          service={bookingService(open, allServices, lang)}
          letter={letterOf(open.serviceId)}
          zone={zone}
          status={mayArrive ? statusOf(open) : undefined}
          askFirst={opened?.ask}
          canCancel={deskActions(open, mayBook).cancel}
          busy={busy}
          onClose={() => setOpened(null)}
          onArrive={(regular) => arrive(open, regular)}
          onCancel={(reason) => cancel(open, reason)}
          onMove={
            change(open)
              ? () => {
                  // The change drawer takes the details drawer's place: one Drawer, one scrim.
                  setOpened(null)
                  change(open)!()
                }
              : undefined
          }
        />
      )}
      {(drawer === 'add' || editing) && (
        <AddBooking
          key={editing?.id ?? 'add'}
          editing={editing ?? undefined}
          zone={zone}
          branchId={branchId}
          startDay={today}
          today={today}
          services={allServices.filter((s) => s.active)}
          letterOf={letterOf}
          declared={declared.filter((f) => f.active)}
          onClose={() => {
            setDrawer('')
            setEditing(null)
          }}
          onBooked={(date, codeText) => {
            setDrawer('')
            toast.show(editing ? say('appointments.done.changed') : say('hall.done.booked', { code: codeText }))
            setEditing(null)
            showDay(date)
          }}
        />
      )}
      {drawer === 'book' && catalogue && catalogue.length > 0 && (
        <BookDrawer
          branchId={branchId}
          services={allServices}
          zone={zone}
          today={today}
          catalogue={catalogue}
          chooseBranch={chooseBranch}
          onClose={() => setDrawer('')}
          onDone={(a) => {
            setDrawer('')
            // Booked for another branch: said, and the page stays on the one in view.
            if (a.branchId !== branchId) {
              const at = catalogue.find((e) => e.branch.id === a.branchId)?.branch.name ?? ''
              toast.show(say('appointments.bookedAt', { branch: at }))
              return
            }
            toast.show(say('hall.done.booked', { code: a.code }))
            showDay(a.businessDate)
          }}
        />
      )}
      {drawer === 'settings' && (
        <KeepSettings
          keepDays={keepDays}
          mayChange={mayChangePolicy}
          onClose={() => setDrawer('')}
          onSaved={(n) => {
            setKeepDays(n)
            setDrawer('')
            toast.show(say('hall.done.saved'))
          }}
        />
      )}
      {moving && (
        <BookDrawer
          branchId={branchId}
          services={allServices}
          zone={zone}
          today={today}
          moving={moving}
          catalogue={catalogue?.length ? catalogue : undefined}
          onClose={() => setMoving(null)}
          onDone={(a) => {
            setMoving(null)
            setOpened(null)
            toast.show(say('booking.moved', { time: time(a.startsAt) }))
            void load().catch((err) => setError(explain(err, lang, 'common.unreachable')))
          }}
        />
      )}
      {toast.view}
    </Chrome>
  )
}

/** One booking opened from its row: its facts and what can be done with it. */
function BookingPanel({
  a,
  who,
  declared,
  service,
  letter,
  zone,
  status,
  askFirst,
  canCancel,
  busy,
  onClose,
  onArrive,
  onCancel,
  onMove,
}: {
  a: Appointment
  who: string
  declared: VisitField[]
  service: string
  letter: string
  zone?: string
  /** Undefined for someone who may not check customers in. */
  status?: RowStatus
  askFirst?: boolean
  canCancel: boolean
  busy: boolean
  onClose: () => void
  onArrive: (regular: boolean) => void
  onCancel: (reason: string) => void
  onMove?: () => void
}) {
  const { lang, say } = useStaffText()
  const [asking, setAsking] = useState(Boolean(askFirst))
  const [why, setWhy] = useState<CancelReason | ''>('')
  const [other, setOther] = useState('')
  const regular = Boolean(status?.regular)
  const canArrive = Boolean(status?.checkIn || regular)
  const values = declared.filter((f) => a.fields?.[f.key])
  return (
    <FormModal
      title={who}
      sub={[formatMoment(a.startsAt, lang, { time: true, zone }), service].filter(Boolean).join(' · ')}
      onClose={onClose}
      closeLabel={say('ui.close')}
      submitLabel={canArrive && !asking ? say(regular ? 'appointments.regular' : 'appointments.checkIn') : undefined}
      onSubmit={canArrive && !asking ? () => onArrive(regular) : undefined}
      busy={busy}
      extra={
        a.state === 'booked' && canCancel && !asking ? (
          <>
            {onMove && (
              <Button variant="secondary" size="sm" onPress={onMove}>
                {say('appointments.change')}
              </Button>
            )}
            <Button variant="ghost" size="sm" className="rx-danger" onPress={() => setAsking(true)}>
              {say('appointments.cancel')}
            </Button>
          </>
        ) : undefined
      }
    >
      {asking && (
        <Confirm
          question={say('appointments.cancelQuestion', { reference: bookingLabel(a) })}
          yes={say('appointments.cancel')}
          no={say('common.keep')}
          busy={busy || !why}
          onYes={() => why && onCancel(cancelReasonText(why, other, (r) => say(CANCEL_REASON_KEY[r])))}
          onNo={() => setAsking(false)}
        >
          <Select className="rx-field" variant="secondary" value={why || null} onChange={(v) => v != null && setWhy(String(v) as CancelReason)}>
            <Label>{say('appointments.why')}</Label>
            <Select.Trigger>
              <Select.Value />
              <Select.Indicator />
            </Select.Trigger>
            <Select.Popover>
              <ListBox>
                {CANCEL_REASONS.map((r) => (
                  <ListBox.Item key={r} id={r} textValue={say(CANCEL_REASON_KEY[r])}>
                    {say(CANCEL_REASON_KEY[r])}
                    <ListBox.ItemIndicator />
                  </ListBox.Item>
                ))}
              </ListBox>
            </Select.Popover>
          </Select>
          {why === 'other' && (
            <TextField className="rx-field" variant="secondary" value={other} onChange={setOther}>
              <Label>{say('appointments.whyOther')}</Label>
              <Input maxLength={200} />
            </TextField>
          )}
        </Confirm>
      )}
      <dl className="desk-sum">
        <div className="desk-sum__wide">
          <dt>{say('hall.service')}</dt>
          <dd>
            {letter && <span className="rx-qt">{letter}</span>}
            {service || '—'}
          </dd>
        </div>
        <div>
          <dt>{say('hall.reference')}</dt>
          <dd>{a.reference}</dd>
        </div>
        <div>
          <dt>{say('hall.code')}</dt>
          <dd>{a.code}</dd>
        </div>
        {a.customerName && (
          <div>
            <dt>{say('booking.name')}</dt>
            <dd>{a.customerName}</dd>
          </div>
        )}
        {a.contact && (
          <div>
            <dt>{say(a.channel === 'sms' ? 'booking.contact.sms' : 'booking.contact.email')}</dt>
            <dd>{a.contact}</dd>
          </div>
        )}
        {a.customerFin && (
          <div>
            <dt>{say('booking.fin')}</dt>
            <dd>{a.customerFin}</dd>
          </div>
        )}
        {sourceKey(a) && (
          <div>
            <dt>{say('booking.sourceLabel')}</dt>
            <dd>
              {say(sourceKey(a)!)}
              {a.client && a.source === 'online' ? <>{' · '}{a.client}</> : ''}
            </dd>
          </div>
        )}
        {values.map((f) => (
          <div key={f.id}>
            <dt>{nameIn(f.name, lang, f.key)}</dt>
            <dd>{a.fields?.[f.key]}</dd>
          </div>
        ))}
        <div>
          <dt>{say('hall.status')}</dt>
          <dd>
            {a.state === 'arrived'
              ? say('appointments.state.arrived', { number: a.displayNumber ?? '' })
              : endedText(a, say)}
          </dd>
        </div>
        {a.state === 'cancelled' && a.cancelReason && (
          <div className="desk-sum__wide">
            <dt>{say('appointments.why')}</dt>
            <dd>{a.cancelReason}</dd>
          </div>
        )}
      </dl>
    </FormModal>
  )
}

/** One booking made by hand, with a reference made here; without a code of its
 *  own, the reference is the customer's code. */
function AddBooking({
  editing,
  zone,
  branchId,
  startDay,
  today,
  services,
  letterOf,
  declared,
  onClose,
  onBooked,
}: {
  /** A file's booking being changed: its reference stays, the rest is saved again. */
  editing?: Appointment
  zone?: string
  branchId: string
  startDay: string
  today: string
  services: Service[]
  letterOf: (serviceId: string) => string
  declared: VisitField[]
  onClose: () => void
  onBooked: (date: string, code: string) => void
}) {
  const { lang, say } = useStaffText()
  const [serviceId, setServiceId] = useState(editing?.serviceId ?? '')
  const [date, setDate] = useState(editing?.businessDate ?? startDay)
  // Opened before the day's list answered: today arrives later and becomes the day.
  useEffect(() => {
    if (startDay) setDate((d) => d || startDay)
  }, [startDay])
  const [time, setTime] = useState(editing ? clockIn(editing.startsAt, zone) : '')
  const [reference, setReference] = useState(() => editing?.reference ?? newReference())
  const [code, setCode] = useState(editing && editing.code !== normaliseCode(editing.reference) ? editing.code : '')
  const [values, setValues] = useState<Record<string, string>>(editing?.fields ?? {})
  const [refused, setRefused] = useState('')
  const [busy, setBusy] = useState(false)
  const service = services.find((s) => s.id === serviceId)
  const valid = !!service && !!date && /^\d{2}:\d{2}$/.test(time) && reference.trim() !== ''
  const tomorrow = today ? addDays(today, 1) : ''
  const shortDay = (d: string) => formatMoment(new Date(`${d}T12:00:00Z`), lang, { date: true, zone: 'UTC' })

  async function save() {
    if (!service) return
    setBusy(true)
    setRefused('')
    try {
      const fields = Object.fromEntries(Object.entries(values).filter(([, v]) => v.trim() !== ''))
      await api.saveAppointment(branchId, reference.trim(), {
        service: service.code,
        date,
        time,
        ...(code.trim() ? { code: code.trim() } : {}),
        ...(Object.keys(fields).length ? { fields } : {}),
      })
      onBooked(date, normaliseCode(code.trim() || reference.trim()))
    } catch (err) {
      const row = bookingRefusal(err)
      setRefused(row ? reasonText(row, lang) : explain(err, lang, 'common.unreachable'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <FormModal
      title={editing ? say('appointments.changeTitle') : say('hall.add')}
      onClose={onClose}
      valid={valid}
      busy={busy}
      wide
      submitLabel={editing ? say('common.save') : time ? say('hall.book', { time }) : say('hall.bookPlain')}
      onSubmit={() => void save()}
      extra={refused ? <span className="appt-error">{refused}</span> : undefined}
    >
      <fieldset className="field">
        <legend>{say('hall.service')}</legend>
        <div className="desk-pick appt-svcs" role="radiogroup">
          {services.map((s) => {
            const letter = letterOf(s.id)
            return (
              <label key={s.id}>
                <input type="radio" name="appt-add-svc" checked={serviceId === s.id} onChange={() => setServiceId(s.id)} />
                <span className="stub stub--sm">{letter}</span>
                <span className="desk-pick__name">{nameIn(s.name, lang, s.code)}</span>
              </label>
            )
          })}
        </div>
      </fieldset>

      <div className="appt-when">
        <fieldset className="field">
          <legend>{say('hall.day')}</legend>
          <div className="appt-days">
            {today && (
              <div className="seg" role="radiogroup">
                {([[today, say('hall.today')], [tomorrow, say('hall.tomorrow')]] as const).map(([d, text]) => (
                  <label key={d}>
                    <input type="radio" name="appt-add-day" checked={date === d} onChange={() => setDate(d)} />
                    <span>
                      {text}, {shortDay(d)}
                    </span>
                  </label>
                ))}
              </div>
            )}
            <input
              className="input appt-date"
              type="date"
              min={today || undefined}
              aria-label={say('hall.day')}
              value={date}
              onChange={(e) => e.target.value && setDate(e.target.value)}
            />
          </div>
        </fieldset>
        <label className="field">
          <span>{say('hall.time')}</span>
          <input className="input appt-time" type="time" value={time} onChange={(e) => setTime(e.target.value)} />
        </label>
      </div>

      {declared.length > 0 && (
        <div className="appt-fields">
          {declared.map((f) => (
            <label key={f.id} className="field">
              <span>{nameIn(f.name, lang, f.key)}</span>
              {f.type === 'choice' ? (
                <select value={values[f.key] ?? ''} onChange={(e) => setValues({ ...values, [f.key]: e.target.value })}>
                  <option value="" />
                  {(f.choices ?? []).map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  type={f.type === 'phone' ? 'tel' : f.type === 'number' ? 'number' : f.type === 'date' ? 'date' : 'text'}
                  maxLength={f.maxLen || undefined}
                  value={values[f.key] ?? ''}
                  onChange={(e) => setValues({ ...values, [f.key]: e.target.value })}
                />
              )}
            </label>
          ))}
        </div>
      )}

      <div className="appt-fields">
        <label className="field">
          <span>{say('hall.reference')}</span>
          <input value={reference} disabled={!!editing} onChange={(e) => setReference(e.target.value)} />
        </label>
        <label className="field">
          <span>{say('hall.code')}</span>
          <input value={code} placeholder={say('hall.codeSame')} onChange={(e) => setCode(e.target.value)} />
        </label>
      </div>
    </FormModal>
  )
}

/** How long bookings are kept (read-only without catalogue.manage), and how booked customers are called. */
function KeepSettings({
  keepDays,
  mayChange,
  onClose,
  onSaved,
}: {
  keepDays: number | null
  mayChange: boolean
  onClose: () => void
  onSaved: (n: number) => void
}) {
  const { lang, say } = useStaffText()
  const [value, setValue] = useState(keepDays === null ? '' : String(keepDays))
  // Opened before the policy answered: its value fills the empty box.
  useEffect(() => {
    if (keepDays !== null) setValue((v) => v || String(keepDays))
  }, [keepDays])
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const n = Number(value)
  const bad = !Number.isInteger(n) || n < 1 || n > 3650

  async function save() {
    if (bad) {
      setError(say('appointments.keepBad'))
      return
    }
    setBusy(true)
    setError('')
    try {
      onSaved((await api.setAppointmentPolicy(n)).keepDays)
    } catch (err) {
      setError(explain(err, lang, 'common.unreachable'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <FormModal
      title={say('appointments.keep')}
      onClose={onClose}
      hideSubmit={!mayChange}
      valid={value !== '' && value !== String(keepDays)}
      busy={busy}
      submitLabel={say('common.save')}
      onSubmit={() => void save()}
    >
      <label className="field field--narrow">
        <span>{say('appointments.keepDays')}</span>
        <input
          type="number"
          min={1}
          max={3650}
          value={value}
          disabled={!mayChange}
          aria-invalid={(value !== '' && bad) || undefined}
          onChange={(e) => setValue(e.target.value)}
        />
      </label>
      {error && <p className="appt-error">{error}</p>}
      {!mayChange && <p className="appt-hint">{say('appointments.keepReadOnly')}</p>}
    </FormModal>
  )
}
